import React, { useState, useEffect } from 'react';
import { UserService } from '../services/api';
import { useSettings } from '../context/SettingsContext';
import { getTerminology } from '../utils/terminology';
import ConfirmationModal from './common/ConfirmationModal';

const ProfileSettings = () => {
    const { settings, updateSettings } = useSettings();
    const [user, setUser] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: '',
        avatar: null,
        bio: ''
    });

    const [appSettings, setAppSettings] = useState({
        currency: 'PKR',
        school_name: '',
        school_address: '',
        school_phone: '',
        school_email: '',
        school_website: '',
        school_type: 'academy',
        academic_year: '',
        academic_session: '',
        passing_percentage: 40,
        student_id_prefix: '',
        payment_accounts: [],
        id_card_primary_color: '#b00020',
        id_card_secondary_color: '#3f4a5c'
    });

    const terminology = React.useMemo(() => {
        return getTerminology(settings.school_type);
    }, [settings.school_type]);

    const terminologyPreview = React.useMemo(() => {
        return getTerminology(appSettings.school_type);
    }, [appSettings.school_type]);

    const [passwords, setPasswords] = useState({
        current_password: '',
        new_password: '',
        new_password_confirmation: ''
    });

    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [activeTab, setActiveTab] = useState('profile'); // profile, password, app-settings
    const [previewImage, setPreviewImage] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [confirmModal, setConfirmModal] = useState({
        show: false,
        title: '',
        message: '',
        action: null,
        type: 'danger'
    });

    const [twoFactorSettings, setTwoFactorSettings] = useState({
        enabled: false,
        method: 'email', // 'email' or 'google'
        qrCode: null
    });

    useEffect(() => {
        if (settings) {
            if (settings.app_logo) {
                let logoPath = settings.app_logo;
                // Remove leading slash if present
                if (logoPath.startsWith('/')) logoPath = logoPath.substring(1);

                // Check for storage prefix or http
                if (!logoPath.startsWith('storage/') && !logoPath.startsWith('http')) {
                    // Assuming logo is in storage/logos/.. if not specified
                    logoPath = `storage/${logoPath}`;
                }

                const logoUrl = logoPath.startsWith('http') ? logoPath : `${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/${logoPath}`;
                setLogoPreview(logoUrl);
            } else {
                setLogoPreview(null);
            }

            setAppSettings(prev => ({
                ...prev,
                currency: settings.currency || prev.currency,
                school_name: settings.school_name || '',
                school_address: settings.school_address || '',
                school_phone: settings.school_phone || '',
                school_email: settings.school_email || '',
                school_website: settings.school_website || '',
                school_type: settings.school_type || 'academy',
                academic_year: settings.academic_year || '',
                academic_session: settings.academic_session || settings.academic_year || '',
                passing_percentage: settings.passing_percentage || 40,
                student_id_prefix: settings.student_id_prefix || '',
                id_card_primary_color: settings.id_card_primary_color || '#b00020',
                id_card_secondary_color: settings.id_card_secondary_color || '#3f4a5c',
                payment_accounts: settings.payment_accounts ? JSON.parse(settings.payment_accounts) : []
            }));
        }
    }, [settings]);

    useEffect(() => {
        // Load user from local storage or API
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        setUser((prevUser) => ({
            ...prevUser,
            ...storedUser,
            bio: storedUser.bio || 'Administrator at Web Hunnar Institute'
        }));

        setTwoFactorSettings({
            enabled: !!storedUser.two_factor_enabled,
            method: storedUser.two_factor_method || 'email',
            qrCode: null
        });
    }, []);

    const handleInputChange = (e) => {
        setUser({ ...user, [e.target.name]: e.target.value });
    };

    const handlePasswordInput = (e) => {
        setPasswords({ ...passwords, [e.target.name]: e.target.value });
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setPreviewImage(URL.createObjectURL(file));

            // Auto upload
            setLoading(true);
            try {
                const formData = new FormData();
                formData.append('email', user.email);
                formData.append('avatar', file);

                const response = await UserService.updateProfile(formData);
                const updatedUser = response.data.user;

                localStorage.setItem('user', JSON.stringify(updatedUser));
                setUser(prev => ({ ...prev, ...updatedUser }));
                window.dispatchEvent(new CustomEvent('profileUpdated', { detail: updatedUser }));

                setSuccessMsg('Profile image updated successfully!');
                setTimeout(() => setSuccessMsg(''), 3000);
            } catch (error) {
                console.error("Error uploading image", error);
                setErrorMsg("Failed to upload image.");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleImageDeleteClick = () => {
        setConfirmModal({
            show: true,
            title: 'Delete Profile Picture?',
            message: 'Are you sure you want to delete your profile picture? This action cannot be undone.',
            action: 'delete_avatar',
            type: 'danger'
        });
    };

    const confirmImageDelete = async () => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('email', user.email);
            formData.append('delete_avatar', 'true');

            const response = await UserService.updateProfile(formData);
            const updatedUser = response.data.user;

            localStorage.setItem('user', JSON.stringify(updatedUser));
            setUser(prev => ({ ...prev, ...updatedUser }));
            setPreviewImage(null);
            window.dispatchEvent(new CustomEvent('profileUpdated', { detail: updatedUser }));

            setSuccessMsg('Profile image deleted successfully!');
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (error) {
            console.error("Error deleting image", error);
            setErrorMsg("Failed to delete image.");
        } finally {
            setLoading(false);
            setConfirmModal(prev => ({ ...prev, show: false }));
        }
    };

    const handleLogoChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            // Instant feedback
            setLogoPreview(URL.createObjectURL(file));
            setLoading(true);
            try {
                const formData = new FormData();
                formData.append('app_logo', file);
                await updateSettings(formData);
                setSuccessMsg('Logo updated successfully!');
                setTimeout(() => setSuccessMsg(''), 3000);
            } catch (error) {
                console.error("Error updating logo", error);
                setErrorMsg('Failed to update logo');
                // Revert preview if failed
                setLogoPreview(settings.app_logo ? `${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}${settings.app_logo}` : null);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleLogoDeleteClick = () => {
        setConfirmModal({
            show: true,
            title: 'Delete App Logo?',
            message: 'Are you sure you want to delete the app logo? This will revert to the default logo.',
            action: 'delete_logo',
            type: 'danger'
        });
    };

    const confirmLogoDelete = async () => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('delete_logo', 'true');
            await updateSettings(formData);
            setAppSettings({ ...appSettings, app_logo: null }); // Clear any pending upload
            setLogoPreview(null); // Clear preview immediately
            setSuccessMsg('App logo deleted successfully!');
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (error) {
            console.error("Error deleting logo", error);
            setErrorMsg('Failed to delete logo');
        } finally {
            setLoading(false);
            setConfirmModal(prev => ({ ...prev, show: false }));
        }
    };

    const handleConfirmAction = () => {
        if (confirmModal.action === 'delete_avatar') {
            confirmImageDelete();
        } else if (confirmModal.action === 'delete_logo') {
            confirmLogoDelete();
        }
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg('');
        try {
            const formData = new FormData();
            formData.append('first_name', user.first_name || '');
            formData.append('last_name', user.last_name || '');
            formData.append('email', user.email);
            formData.append('phone', user.phone || '');
            formData.append('bio', user.bio || '');

            if (user.avatar instanceof File) {
                formData.append('avatar', user.avatar);
            }

            const response = await UserService.updateProfile(formData);
            const updatedUser = response.data.user;

            localStorage.setItem('user', JSON.stringify(updatedUser));
            setUser(prev => ({ ...prev, ...updatedUser }));
            window.dispatchEvent(new CustomEvent('profileUpdated', { detail: updatedUser }));

            setSuccessMsg('Profile updated successfully!');
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (error) {
            console.error("Error updating profile", error);
            setErrorMsg(error.response?.data?.message || "Failed to update profile.");
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (passwords.new_password !== passwords.new_password_confirmation) {
            setErrorMsg("New passwords do not match");
            return;
        }
        setLoading(true);
        setErrorMsg('');
        try {
            await UserService.changePassword({
                current_password: passwords.current_password,
                new_password: passwords.new_password,
                new_password_confirmation: passwords.new_password_confirmation
            });
            setSuccessMsg('Password changed successfully!');
            setPasswords({ current_password: '', new_password: '', new_password_confirmation: '' });
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (error) {
            setErrorMsg(error.response?.data?.message || 'Error changing password');
        } finally {
            setLoading(false);
        }
    };

    const handleAddAccount = () => {
        setAppSettings(prev => ({
            ...prev,
            payment_accounts: [...prev.payment_accounts, { name: '', number: '', type: 'bank' }]
        }));
    };

    const handleRemoveAccount = (index) => {
        setAppSettings(prev => ({
            ...prev,
            payment_accounts: prev.payment_accounts.filter((_, i) => i !== index)
        }));
    };

    const handleAccountChange = (index, field, value) => {
        const newAccounts = [...appSettings.payment_accounts];
        newAccounts[index][field] = value;
        setAppSettings(prev => ({ ...prev, payment_accounts: newAccounts }));
    };

    const handleSettingsSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const formData = new FormData();
            if (appSettings.currency) formData.append('currency', appSettings.currency);
            if (appSettings.school_name) formData.append('school_name', appSettings.school_name);
            if (appSettings.school_address) formData.append('school_address', appSettings.school_address);
            if (appSettings.school_phone) formData.append('school_phone', appSettings.school_phone);
            if (appSettings.school_email) formData.append('school_email', appSettings.school_email);
            if (appSettings.school_website) formData.append('school_website', appSettings.school_website);
            if (appSettings.school_type) formData.append('school_type', appSettings.school_type);
            if (appSettings.academic_year) formData.append('academic_year', appSettings.academic_year);
            if (appSettings.academic_session) formData.append('academic_session', appSettings.academic_session);
            if (appSettings.passing_percentage) formData.append('passing_percentage', appSettings.passing_percentage);
            if (appSettings.student_id_prefix) formData.append('student_id_prefix', appSettings.student_id_prefix);
            if (appSettings.id_card_primary_color) formData.append('id_card_primary_color', appSettings.id_card_primary_color);
            if (appSettings.id_card_secondary_color) formData.append('id_card_secondary_color', appSettings.id_card_secondary_color);
            formData.append('payment_accounts', JSON.stringify(appSettings.payment_accounts));

            await updateSettings(formData);
            setSuccessMsg('App settings updated successfully!');
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (error) {
            setErrorMsg('Error updating settings');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handle2FASubmit = async () => {
        setLoading(true);
        setErrorMsg('');
        try {
            const formData = new FormData();
            formData.append('email', user.email);
            formData.append('two_factor_enabled', twoFactorSettings.enabled ? 1 : 0);
            formData.append('two_factor_method', twoFactorSettings.method);

            const response = await UserService.updateProfile(formData);
            const updatedUser = response.data.user;

            localStorage.setItem('user', JSON.stringify(updatedUser));
            setUser(prev => ({ ...prev, ...updatedUser }));
            window.dispatchEvent(new CustomEvent('profileUpdated', { detail: updatedUser }));

            setSuccessMsg('2FA settings updated successfully!');
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (error) {
            console.error("Error updating 2FA", error);
            setErrorMsg(error.response?.data?.message || "Failed to update 2FA settings.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container-fluid p-0">
            <div className="section-header mb-5">
                <div>
                    <h2 className="fw-bold">Settings</h2>
                    <p className="text-muted">Manage profile, security, and application preferences</p>
                </div>
            </div>

            <div className="card-box border-0 shadow-sm p-0 overflow-hidden bg-white mb-5 mx-auto" style={{ maxWidth: '1000px' }}>
                {/* 1. Top Section: Banner & Profile Info */}
                <div className="position-relative">
                    {/* Banner */}
                    <div style={{ height: '180px', background: 'linear-gradient(135deg, #4361ee 0%, #a2a8d3 100%)' }}>
                        <div className="position-absolute w-100 h-100 opacity-25" style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '10px 10px' }}></div>
                    </div>

                    {/* Avatar Container - Centered */}
                    <div className="position-absolute start-50 translate-middle" style={{ top: '180px' }}>
                        <div className="d-inline-block position-relative">
                            <div
                                className="avatar-xl rounded-circle border border-4 border-white shadow-lg overflow-hidden d-flex align-items-center justify-content-center bg-white text-primary fw-bold"
                                style={{ width: '140px', height: '140px', fontSize: '3rem' }}
                            >
                                {(previewImage || (user.avatar && typeof user.avatar === 'string')) ? (
                                    <img
                                        src={previewImage || (user.avatar.startsWith('http') ? user.avatar : `${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}${user.avatar}`)}
                                        alt="Profile"
                                        className="w-100 h-100 object-fit-cover"
                                    />
                                ) : (
                                    user.first_name?.charAt(0) || 'A'
                                )}
                            </div>
                            <label
                                htmlFor="avatar-upload"
                                className="position-absolute bottom-0 end-0 bg-primary text-white rounded-circle shadow-sm cursor-pointer d-flex align-items-center justify-content-center hover-up"
                                style={{ width: '40px', height: '40px', border: '3px solid #fff' }}
                                title="Upload New Photo"
                            >
                                <i className="bi bi-camera-fill small"></i>
                            </label>
                            {user.avatar && (
                                <button
                                    onClick={handleImageDeleteClick}
                                    className="position-absolute top-0 end-0 bg-danger text-white rounded-circle border-0 shadow-sm d-flex align-items-center justify-content-center hover-up"
                                    style={{ width: '30px', height: '30px', transform: 'translate(10%, 10%)', border: '2px solid #fff' }}
                                    title="Delete Photo"
                                    type="button"
                                >
                                    <i className="bi bi-trash-fill small" style={{ fontSize: '0.8rem' }}></i>
                                </button>
                            )}
                            <input type="file" id="avatar-upload" className="d-none" accept="image/*" onChange={handleImageChange} />
                        </div>
                    </div>
                </div>

                {/* Profile Text Info */}
                <div className="pt-5 mt-5 text-center px-4 pb-4">
                    <h3 className="fw-bold text-dark mb-1">{user.first_name} {user.last_name}</h3>
                    <div className="mb-3">
                        <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2 rounded-pill fw-bold" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                            {user.role || 'Administrator'}
                        </span>
                    </div>
                    <p className="text-secondary small mb-0 px-2 line-clamp-2 mx-auto" style={{ maxWidth: '600px' }}>
                        {user.bio || 'No bio added yet.'}
                    </p>
                </div>

                {/* 2. Tabs Navigation - Centered */}
                <div className="d-flex justify-content-center border-bottom mt-2 gap-4 px-4 flex-wrap">
                    <button
                        className={`btn border-0 py-3 px-1 fw-bold position-relative rounded-0 ${activeTab === 'profile' ? 'text-primary' : 'text-muted'}`}
                        onClick={() => setActiveTab('profile')}
                        style={{ transition: 'all 0.3s' }}
                    >
                        <span className="d-flex align-items-center gap-2">
                            <i className={`bi ${activeTab === 'profile' ? 'bi-person-fill' : 'bi-person'}`}></i> Personal Info
                        </span>
                        {activeTab === 'profile' && (
                            <div className="position-absolute bottom-0 start-0 w-100 bg-primary rounded-top" style={{ height: '3px' }}></div>
                        )}
                    </button>
                    <button
                        className={`btn border-0 py-3 px-1 fw-bold position-relative rounded-0 ${activeTab === 'security' ? 'text-primary' : 'text-muted'}`}
                        onClick={() => setActiveTab('security')}
                        style={{ transition: 'all 0.3s' }}
                    >
                        <span className="d-flex align-items-center gap-2">
                            <i className={`bi ${activeTab === 'security' ? 'bi-shield-fill-check' : 'bi-shield-check'}`}></i> Security
                        </span>
                        {activeTab === 'security' && (
                            <div className="position-absolute bottom-0 start-0 w-100 bg-primary rounded-top" style={{ height: '3px' }}></div>
                        )}
                    </button>
                    <button
                        className={`btn border-0 py-3 px-1 fw-bold position-relative rounded-0 ${activeTab === '2fa' ? 'text-primary' : 'text-muted'}`}
                        onClick={() => setActiveTab('2fa')}
                        style={{ transition: 'all 0.3s' }}
                    >
                        <span className="d-flex align-items-center gap-2">
                            <i className={`bi ${activeTab === '2fa' ? 'bi-shield-lock-fill' : 'bi-shield-lock'}`}></i> 2FA
                        </span>
                        {activeTab === '2fa' && (
                            <div className="position-absolute bottom-0 start-0 w-100 bg-primary rounded-top" style={{ height: '3px' }}></div>
                        )}
                    </button>
                    <button
                        className={`btn border-0 py-3 px-1 fw-bold position-relative rounded-0 ${activeTab === 'institute' ? 'text-primary' : 'text-muted'}`}
                        onClick={() => setActiveTab('institute')}
                        style={{ transition: 'all 0.3s' }}
                    >
                        <span className="d-flex align-items-center gap-2">
                            <i className={`bi ${activeTab === 'institute' ? 'bi-building-fill' : 'bi-building'}`}></i> {terminologyPreview.institution} Details
                        </span>
                        {activeTab === 'institute' && (
                            <div className="position-absolute bottom-0 start-0 w-100 bg-primary rounded-top" style={{ height: '3px' }}></div>
                        )}
                    </button>
                    <button
                        className={`btn border-0 py-3 px-1 fw-bold position-relative rounded-0 ${activeTab === 'app-settings' ? 'text-primary' : 'text-muted'}`}
                        onClick={() => setActiveTab('app-settings')}
                        style={{ transition: 'all 0.3s' }}
                    >
                        <span className="d-flex align-items-center gap-2">
                            <i className={`bi ${activeTab === 'app-settings' ? 'bi-sliders' : 'bi-sliders2'}`}></i> App Settings
                        </span>
                        {activeTab === 'app-settings' && (
                            <div className="position-absolute bottom-0 start-0 w-100 bg-primary rounded-top" style={{ height: '3px' }}></div>
                        )}
                    </button>
                </div>

                {/* 3. Tab Content */}
                <div className="p-4 p-md-5">
                    <div className="row justify-content-center">
                        <div className="col-lg-8">

                            {successMsg && <div className="alert alert-success rounded-3 border-0 bg-success-subtle text-success d-flex align-items-center mb-4"><i className="bi bi-check-circle-fill me-2 fs-5"></i>{successMsg}</div>}
                            {errorMsg && <div className="alert alert-danger rounded-3 border-0 bg-danger-subtle text-danger d-flex align-items-center mb-4"><i className="bi bi-exclamation-triangle-fill me-2 fs-5"></i>{errorMsg}</div>}

                            {activeTab === 'profile' && (
                                <form onSubmit={handleProfileSubmit} className="fade-in">
                                    <div className="row g-4 mb-4">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label className="form-label">First Name</label>
                                                <input type="text" className="form-control" name="first_name" value={user.first_name || ''} onChange={handleInputChange} placeholder="John" />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label className="form-label">Last Name</label>
                                                <input type="text" className="form-control" name="last_name" value={user.last_name || ''} onChange={handleInputChange} placeholder="Doe" />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label className="form-label">Email Address</label>
                                                <input type="email" className="form-control" name="email" value={user.email || ''} onChange={handleInputChange} readOnly style={{ backgroundColor: '#f8fafc', cursor: 'not-allowed' }} />
                                                <small className="text-muted ms-1 mt-1 d-block" style={{ fontSize: '10px' }}>Email cannot be changed directly.</small>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label className="form-label">Phone Number</label>
                                                <input type="text" className="form-control" name="phone" value={user.phone || ''} onChange={handleInputChange} placeholder="+1 234 567 890" />
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="form-group">
                                                <label className="form-label">Bio / About</label>
                                                <textarea className="form-control" name="bio" value={user.bio || ''} onChange={handleInputChange} rows="4" placeholder="Tell us a bit about yourself..."></textarea>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-end border-top pt-4">
                                        <button type="submit" className="btn btn-primary px-5 d-flex align-items-center gap-2 shadow-sm rounded-pill" disabled={loading}>
                                            {loading ? 'Saving...' : <><i className="bi bi-check-lg"></i> Save Changes</>}
                                        </button>
                                    </div>
                                </form>
                            )}

                            {activeTab === 'security' && (
                                <div className="fade-in">
                                    {/* Password Change Section */}
                                    <div className="mb-5">
                                        <h5 className="fw-bold mb-3">Change Password</h5>
                                        <form onSubmit={handlePasswordSubmit}>
                                            <div className="row g-4">
                                                <div className="col-12">
                                                    <div className="p-3 mb-3 bg-light-subtle border border-light-subtle rounded-3 d-flex gap-3 align-items-center">
                                                        <div className="bg-white p-2 rounded-circle shadow-sm text-primary">
                                                            <i className="bi bi-shield-check fs-5"></i>
                                                        </div>
                                                        <div>
                                                            <h6 className="fw-bold mb-1 text-dark">Secure Your Account</h6>
                                                            <p className="text-secondary small mb-0">Use a strong password to protect your account.</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-12">
                                                    <label className="form-label">Current Password</label>
                                                    <input type="password" className="form-control" name="current_password" value={passwords.current_password || ''} onChange={handlePasswordInput} required />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label">New Password</label>
                                                    <input type="password" className="form-control" name="new_password" value={passwords.new_password || ''} onChange={handlePasswordInput} required />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label">Confirm New Password</label>
                                                    <input type="password" className="form-control" name="new_password_confirmation" value={passwords.new_password_confirmation || ''} onChange={handlePasswordInput} required />
                                                </div>
                                            </div>
                                            <div className="d-flex justify-content-end border-top pt-4 mt-4">
                                                <button type="submit" className="btn btn-primary px-5 shadow-sm rounded-pill" disabled={loading}>
                                                    {loading ? 'Updating...' : 'Update Password'}
                                                </button>
                                            </div>
                                        </form>
                                    </div>

                                </div>
                            )}

                            {activeTab === '2fa' && (
                                <div className="fade-in">
                                    {/* 2FA Section */}
                                    <div className="p-3 mb-4 bg-light-subtle border border-light-subtle rounded-3 d-flex gap-3 align-items-center">
                                        <div className="bg-white p-2 rounded-circle shadow-sm text-primary">
                                            <i className="bi bi-shield-lock fs-5"></i>
                                        </div>
                                        <div>
                                            <h6 className="fw-bold mb-1 text-dark">Two-Factor Authentication</h6>
                                            <p className="text-secondary small mb-0">Add an extra layer of security to your account</p>
                                        </div>
                                    </div>

                                    {/* 2FA Toggle */}
                                    <div className="card border-0 shadow-sm mb-4">
                                        <div className="card-body p-4">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <div>
                                                    <h6 className="fw-bold mb-1">Enable 2FA</h6>
                                                    <small className="text-muted">Require a verification code when logging in</small>
                                                </div>
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        id="enable2FA"
                                                        checked={twoFactorSettings.enabled}
                                                        onChange={(e) => setTwoFactorSettings({ ...twoFactorSettings, enabled: e.target.checked })}
                                                        style={{ width: '3rem', height: '1.5rem', cursor: 'pointer' }}
                                                    />
                                                </div>
                                            </div>

                                            {twoFactorSettings.enabled && (
                                                <div className="mt-4 pt-3 border-top">
                                                    <label className="form-label fw-bold mb-3">Authentication Method</label>

                                                    {/* Email Method */}
                                                    <div className="form-check mb-3 p-3 border rounded-3 hover-shadow" style={{ cursor: 'pointer' }}>
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="2faMethod"
                                                            id="emailMethod"
                                                            value="email"
                                                            checked={twoFactorSettings.method === 'email'}
                                                            onChange={(e) => setTwoFactorSettings({ ...twoFactorSettings, method: e.target.value })}
                                                        />
                                                        <label className="form-check-label w-100" htmlFor="emailMethod" style={{ cursor: 'pointer' }}>
                                                            <div className="d-flex align-items-start gap-3">
                                                                <i className="bi bi-envelope-fill text-primary fs-4 mt-1"></i>
                                                                <div>
                                                                    <div className="fw-bold">Email Verification</div>
                                                                    <small className="text-muted">Receive a 6-digit code via email</small>
                                                                </div>
                                                            </div>
                                                        </label>
                                                    </div>

                                                    {/* Google Authenticator Method */}
                                                    <div className="form-check p-3 border rounded-3 hover-shadow" style={{ cursor: 'pointer' }}>
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="2faMethod"
                                                            id="googleMethod"
                                                            value="google"
                                                            checked={twoFactorSettings.method === 'google'}
                                                            onChange={(e) => setTwoFactorSettings({ ...twoFactorSettings, method: e.target.value })}
                                                        />
                                                        <label className="form-check-label w-100" htmlFor="googleMethod" style={{ cursor: 'pointer' }}>
                                                            <div className="d-flex align-items-start gap-3">
                                                                <i className="bi bi-google text-danger fs-4 mt-1"></i>
                                                                <div>
                                                                    <div className="fw-bold">Google Authenticator</div>
                                                                    <small className="text-muted">Use the Google Authenticator app</small>
                                                                </div>
                                                            </div>
                                                        </label>
                                                    </div>

                                                    {/* Google Authenticator QR Code */}
                                                    {twoFactorSettings.method === 'google' && (
                                                        <div className="mt-4 p-4 bg-light rounded-3 text-center">
                                                            <h6 className="fw-bold mb-3">Scan QR Code</h6>
                                                            <div className="bg-white p-3 rounded d-inline-block shadow-sm mb-3">
                                                                {twoFactorSettings.qrCode ? (
                                                                    <img src={twoFactorSettings.qrCode} alt="QR Code" style={{ width: '200px', height: '200px' }} />
                                                                ) : (
                                                                    <div className="d-flex align-items-center justify-content-center" style={{ width: '200px', height: '200px' }}>
                                                                        <i className="bi bi-qr-code text-muted" style={{ fontSize: '4rem' }}></i>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <p className="small text-muted mb-0">Scan this QR code with Google Authenticator app</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-end border-top pt-4">
                                        <button
                                            type="button"
                                            className="btn btn-primary px-5 shadow-sm rounded-pill"
                                            disabled={loading}
                                            onClick={handle2FASubmit}
                                        >
                                            {loading ? 'Saving...' : 'Save Security Settings'}
                                        </button>
                                    </div>

                                </div>
                            )}

                            {activeTab === 'institute' && (
                                <form onSubmit={handleSettingsSubmit} className="fade-in">
                                    <div className="mb-4">
                                        <label className="form-label">{terminologyPreview.institution} Logo</label>
                                        <div className="d-flex align-items-center gap-4 p-4 border rounded-3 bg-light border-dashed position-relative">
                                            {logoPreview ? (
                                                <div className="position-relative">
                                                    <div className="p-2 bg-white rounded border shadow-sm">
                                                        <img src={logoPreview} alt="Logo Preview" style={{ maxHeight: '80px' }} />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={handleLogoDeleteClick}
                                                        className="btn btn-danger btn-sm position-absolute rounded-circle shadow-sm border-2 border-white"
                                                        style={{ top: '-10px', right: '-10px', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                        title="Delete Logo"
                                                    >
                                                        <i className="bi bi-trash-fill" style={{ fontSize: '0.8rem' }}></i>
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="p-4 bg-white rounded border border-dashed text-center text-muted">
                                                    <i className="bi bi-image fs-1 d-block mb-1"></i>
                                                    <small>No Logo Selected</small>
                                                </div>
                                            )}
                                            <div className="flex-grow-1">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <h6 className="fw-bold mb-0 text-dark">Upload New Logo</h6>
                                                    {logoPreview && <span className="badge bg-light text-secondary border">Current Logo Active</span>}
                                                </div>
                                                <p className="text-secondary small mb-3">Supported formats: PNG, JPG. Max size: 2MB.</p>
                                                <input type="file" className="form-control bg-white" accept="image/*" onChange={handleLogoChange} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card shadow-sm border-0 mb-4">
                                        <div className="card-body">
                                            <div className="mb-3">
                                                <label className="form-label">{terminologyPreview.institution} Name</label>
                                                <input type="text" className="form-control" value={appSettings.school_name || ''} onChange={(e) => setAppSettings({ ...appSettings, school_name: e.target.value })} placeholder="" />
                                            </div>
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Phone Number</label>
                                                    <input type="text" className="form-control" value={appSettings.school_phone || ''} onChange={(e) => setAppSettings({ ...appSettings, school_phone: e.target.value })} placeholder="" />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Email Address</label>
                                                    <input type="email" className="form-control" value={appSettings.school_email || ''} onChange={(e) => setAppSettings({ ...appSettings, school_email: e.target.value })} placeholder="" />
                                                </div>
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Address</label>
                                                <textarea className="form-control" rows="2" value={appSettings.school_address || ''} onChange={(e) => setAppSettings({ ...appSettings, school_address: e.target.value })} placeholder=""></textarea>
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Website URL</label>
                                                <input type="url" className="form-control" value={appSettings.school_website || ''} onChange={(e) => setAppSettings({ ...appSettings, school_website: e.target.value })} placeholder="" />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label fw-bold">Institution Type</label>
                                                <div className="p-3 border rounded-3 bg-light-subtle">
                                                    <div className="row g-3">
                                                        {[
                                                            { id: 'academy', label: '🎓 Academy', desc: 'Courses & Batches' },
                                                            { id: 'school', label: '🏫 School', desc: 'Classes & Sections' },
                                                            { id: 'institute', label: '🏢 Institute', desc: 'Courses & Batches' }
                                                        ].map((type) => (
                                                            <div className="col-md-4" key={type.id}>
                                                                <div
                                                                    className={`p-3 rounded-3 border text-center cursor-pointer transition-all ${appSettings.school_type === type.id ? 'bg-primary text-white border-primary shadow-sm' : 'bg-white text-dark border-light'}`}
                                                                    onClick={() => setAppSettings({ ...appSettings, school_type: type.id })}
                                                                    style={{ cursor: 'pointer' }}
                                                                >
                                                                    <div className="fw-bold mb-1">{type.label}</div>
                                                                    <div className={`small ${appSettings.school_type === type.id ? 'text-white-50' : 'text-muted'}`}>{type.desc}</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <small className="text-muted d-block mt-3">
                                                        <i className="bi bi-info-circle me-1"></i>
                                                        Changing this will update terminology (e.g., Classes vs Courses) across the platform.
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-end border-top pt-4">
                                        <button type="submit" className="btn btn-primary px-5 shadow-sm rounded-pill" disabled={loading}>
                                            {loading ? 'Saving...' : `Save ${terminologyPreview.institution} Details`}
                                        </button>
                                    </div>
                                </form>
                            )}

                            {activeTab === 'app-settings' && (
                                <form onSubmit={handleSettingsSubmit} className="fade-in">
                                    <div className="mb-4">
                                        <label className="form-label">Default Currency</label>
                                        <select
                                            className="form-select bg-light border-0"
                                            value={appSettings.currency || 'PKR'}
                                            onChange={(e) => setAppSettings({ ...appSettings, currency: e.target.value })}
                                        >
                                            <option value="USD">USD ($)</option>
                                            <option value="EUR">EUR (€)</option>
                                            <option value="GBP">GBP (£)</option>
                                            <option value="PKR">PKR (Rs)</option>
                                            <option value="INR">INR (₹)</option>
                                            <option value="AED">AED (dh)</option>
                                        </select>
                                        <small className="text-muted">This currency will be used across the application.</small>
                                    </div>

                                    <div className="mb-4">
                                        <label className="form-label">Current Academic Session</label>
                                        <input
                                            type="text"
                                            className="form-control bg-light border-0"
                                            placeholder="e.g. 2026-2027 or Fall 2026"
                                            value={appSettings.academic_session || appSettings.academic_year || ''}
                                            onChange={(e) => setAppSettings({ ...appSettings, academic_session: e.target.value, academic_year: e.target.value })}
                                        />
                                        <small className="text-muted">Set the current active academic session for records and reports.</small>
                                    </div>

                                    <div className="mb-4">
                                        <label className="form-label text-dark fw-bold">Student ID Prefix</label>
                                        <input
                                            type="text"
                                            className="form-control bg-light border-0"
                                            placeholder="e.g. MGS"
                                            value={appSettings.student_id_prefix || ''}
                                            onChange={(e) => setAppSettings({ ...appSettings, student_id_prefix: e.target.value })}
                                        />
                                        <small className="text-secondary opacity-75 d-block mt-1">This prefix will be used for new student IDs (e.g. <span className="text-primary fw-bold">{appSettings.student_id_prefix || 'SCH'}-{new Date().getFullYear()}-001</span>).</small>
                                    </div>

                                    <div className="mb-4">
                                        <label className="form-label">Passing Grade (%)</label>
                                        <input
                                            type="number"
                                            className="form-control bg-light border-0"
                                            placeholder="e.g. 40"
                                            min="0"
                                            max="100"
                                            value={appSettings.passing_percentage || ''}
                                            onChange={(e) => setAppSettings({ ...appSettings, passing_percentage: e.target.value })}
                                        />
                                        <small className="text-muted">Minimum percentage required for a student to pass an exam.</small>
                                    </div>

                                    <div className="mb-5">
                                        <div className="p-4 bg-light rounded-4 border">
                                            <h5 className="fw-bold mb-3 d-flex align-items-center">
                                                <i className="bi bi-person-badge text-primary me-2"></i> ID Card Design
                                            </h5>
                                            <div className="row g-4">
                                                <div className="col-md-6">
                                                    <label className="form-label text-dark fw-bold">Primary Color</label>
                                                    <div className="d-flex align-items-center gap-3">
                                                        <input
                                                            type="color"
                                                            className="form-control form-control-color p-1 shadow-sm"
                                                            value={appSettings.id_card_primary_color || '#b00020'}
                                                            onChange={(e) => setAppSettings({ ...appSettings, id_card_primary_color: e.target.value })}
                                                            title="Choose primary color"
                                                        />
                                                        <span className="text-muted small">Main color for accents and border</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label text-dark fw-bold">Secondary Color</label>
                                                    <div className="d-flex align-items-center gap-3">
                                                        <input
                                                            type="color"
                                                            className="form-control form-control-color p-1 shadow-sm"
                                                            value={appSettings.id_card_secondary_color || '#3f4a5c'}
                                                            onChange={(e) => setAppSettings({ ...appSettings, id_card_secondary_color: e.target.value })}
                                                            title="Choose secondary color"
                                                        />
                                                        <span className="text-muted small">Secondary accent color</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <label className="form-label mb-0 fw-bold">Payment Receiving Accounts</label>
                                            <button type="button" className="btn btn-sm btn-outline-primary rounded-pill px-3" onClick={handleAddAccount}>
                                                <i className="bi bi-plus-lg me-1"></i> Add Account
                                            </button>
                                        </div>
                                        <p className="text-muted small mb-3">Add accounts (Bank, JazzCash, EasyPaisa) where you receive fee transfers. These will be selectable during fee collection.</p>
                                        
                                        <div className="d-flex flex-column gap-3">
                                            {appSettings.payment_accounts.map((account, index) => (
                                                <div key={index} className="p-3 border rounded-3 bg-light-subtle shadow-sm transition-all position-relative">
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-sm btn-light text-danger position-absolute top-0 end-0 m-2 rounded-circle shadow-sm"
                                                        onClick={() => handleRemoveAccount(index)}
                                                        style={{ width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                    >
                                                        <i className="bi bi-trash-fill small"></i>
                                                    </button>
                                                    
                                                    <div className="row g-3">
                                                        <div className="col-md-4">
                                                            <label className="form-label small text-muted">Account Name</label>
                                                            <input 
                                                                type="text" 
                                                                className="form-control form-control-sm" 
                                                                value={account.name} 
                                                                onChange={(e) => handleAccountChange(index, 'name', e.target.value)}
                                                                placeholder="e.g. HBL Main"
                                                            />
                                                        </div>
                                                        <div className="col-md-5">
                                                            <label className="form-label small text-muted">Account Number / Detail</label>
                                                            <input 
                                                                type="text" 
                                                                className="form-control form-control-sm" 
                                                                value={account.number} 
                                                                onChange={(e) => handleAccountChange(index, 'number', e.target.value)}
                                                                placeholder="e.g. 1234-5678-9012"
                                                            />
                                                        </div>
                                                        <div className="col-md-3">
                                                            <label className="form-label small text-muted">Type</label>
                                                            <select 
                                                                className="form-select form-select-sm"
                                                                value={account.type}
                                                                onChange={(e) => handleAccountChange(index, 'type', e.target.value)}
                                                            >
                                                                <option value="bank">Bank</option>
                                                                <option value="jazzcash">JazzCash</option>
                                                                <option value="easypaisa">EasyPaisa</option>
                                                                <option value="other">Other</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            {appSettings.payment_accounts.length === 0 && (
                                                <div className="text-center py-4 bg-light border border-dashed rounded-3 text-muted">
                                                    <i className="bi bi-credit-card-2-front fs-2 d-block mb-2"></i>
                                                    <p className="mb-0">No payment accounts added yet.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-end border-top pt-4">
                                        <button type="submit" className="btn btn-primary px-5 shadow-sm rounded-pill" disabled={loading}>
                                            {loading ? 'Saving...' : 'Save App Settings'}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmationModal
                show={confirmModal.show}
                title={confirmModal.title}
                message={confirmModal.message}
                type={confirmModal.type}
                loading={loading}
                onConfirm={handleConfirmAction}
                onCancel={() => setConfirmModal(prev => ({ ...prev, show: false }))}
                confirmText="Yes, Delete"
            />
        </div>
    );
};

export default ProfileSettings;
